import { useState } from "react";
import { Loader2, Send, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import { localApi } from "../lib/localApi";
import { formatError } from "../lib/errors";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const field = "w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-brass/60 focus:ring-2 focus:ring-brass/20 transition-colors";
const EMPTY = { name: "", company: "", city: "", role: "", email: "", rating: 5, text: "", consent: false, website: "" };

export function TestimonialDialog({ open, onOpenChange }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await localApi.post("/public/testimonials", { ...f, rating: Number(f.rating) });
      setDone(true);
    } catch (err) { toast.error(formatError(err, "Envoi impossible")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setDone(false); setF(EMPTY); } }}>
      <DialogContent className="bg-[#071e42] border-white/10 text-white sm:max-w-lg" data-testid="testimonial-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Votre avis sur Casanéo</DialogTitle>
          <DialogDescription className="text-slate-400">Vous gérez une conciergerie ou une agence avec Casanéo ? Racontez votre expérience : nous la relisons puis la publions sur casaneo.pro.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center" data-testid="testimonial-success">
            <CheckCircle2 className="h-10 w-10 text-brass mx-auto" />
            <p className="mt-4 text-white">Merci ! Votre témoignage a bien été reçu.</p>
            <p className="text-sm text-slate-400 mt-1">Il sera publié après relecture, sous 48 h ouvrées.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="testimonial-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={field} placeholder="Votre nom *" value={f.name} onChange={set("name")} required minLength={2} data-testid="testimonial-name" />
              <input className={field} placeholder="Société / conciergerie *" value={f.company} onChange={set("company")} required minLength={2} data-testid="testimonial-company" />
              <input className={field} placeholder="Ville" value={f.city} onChange={set("city")} data-testid="testimonial-city" />
              <input className={field} placeholder="Fonction (gérant, gestionnaire…)" value={f.role} onChange={set("role")} data-testid="testimonial-role" />
            </div>
            <input className={field} type="email" placeholder="E-mail professionnel * (non publié)" value={f.email} onChange={set("email")} required data-testid="testimonial-email" />
            <div className="flex items-center gap-1" data-testid="testimonial-rating">
              <span className="text-xs text-slate-400 mr-2">Note</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setF((s) => ({ ...s, rating: n }))} aria-label={`${n} étoiles`} data-testid={`testimonial-star-${n}`}>
                  <Star className={`h-5 w-5 text-brass ${n <= f.rating ? "fill-brass" : "opacity-30"}`} />
                </button>
              ))}
            </div>
            <textarea className={`${field} min-h-[120px]`} placeholder="Votre témoignage (30 caractères minimum) *" value={f.text} onChange={set("text")} required minLength={30} maxLength={1500} data-testid="testimonial-text" />
            <input className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
            <label className="flex items-start gap-2 text-xs text-slate-400">
              <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5 accent-[#c9a961]" data-testid="testimonial-consent" />
              J'autorise Casanéo (SAS MHP Immobilier) à publier ce témoignage avec mon nom et ma société sur casaneo.pro.
            </label>
            <button type="submit" disabled={busy || !f.consent} className="w-full rounded-xl bg-brass text-[#071e42] font-medium py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="testimonial-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer mon témoignage
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
